import React, { FC, useCallback, useContext, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { AssignLicenses } from './AssignLicenses';
import { IAssignLicensePayload } from '../../hooks/useUsersApi';
import { useTranslation } from 'react-i18next';
import { LicensesContext } from '../../contexts/LicensesContext';
import { UserContext } from '../../contexts/UserContext';
import { apiLicense } from '../../resources/api/apiLicense';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { UnassignedLicensesFetcher } from '../../fetchers/UnassignedLicensesFetcher';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';

interface IProps {
  open: boolean;
  closeDialog: () => void;
}

const { useAssignLicense } = apiLicense;

export const AssignLicensesDialog: FC<IProps> = ({ open, closeDialog }) => {
  const { user } = useContext(UserContext);
  const { organization } = useContext(OrganizationContext);
  const handleApiError = useApiErrorHandler();

  const { onRefresh } = useContext(LicensesContext);
  const assignLicensesPayloadRef = useRef<IAssignLicensePayload[]>([]);

  const { execute: assignLicense } = useAssignLicense(organization ?? void 0, user?.id);

  const [isAnyLicenseToSubmit, setAnyLicenseToSubmit] = useState(false);
  const { t } = useTranslation();

  const updateLicensesPayload = useCallback((data: IAssignLicensePayload[]) => {
    assignLicensesPayloadRef.current = data;
    setAnyLicenseToSubmit(data.length > 0);
  }, []);

  const submitAssignLicenses = useCallback(async () => {
    try {
      if (!user || assignLicensesPayloadRef.current.length === 0) return;
      for (const payload of assignLicensesPayloadRef.current) {
        await assignLicense(payload);
      }
      await onRefresh();
      closeDialog();
    } catch (e) {
      handleApiError(e);
    }
  }, [user?.id, assignLicense, closeDialog]);

  if (!user) return null;

  return (
    <Dialog open={open} onClose={closeDialog} maxWidth="md">
      <DialogTitle>{t('assignLicenses.title')}</DialogTitle>
      <DialogContent>
        <UnassignedLicensesFetcher.WAF>
          <AssignLicenses onFormPayloadUpdate={updateLicensesPayload} />
        </UnassignedLicensesFetcher.WAF>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>{t('common.dialog.close')}</Button>
        <Button variant="contained" color="primary" onClick={submitAssignLicenses} disabled={!isAnyLicenseToSubmit}>
          {t('common.dialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
